import BotoesSalvarExcluir from '@/shared/components/forms/BotoesSalvarExcluir';
import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import ModalHistorico from '@/modules/system/historico/ModalHistorico';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { CreateSetorDto, ReadPredioDto, ReadSetorDto } from '@/api.types';
import { DeleteSetor, GetSetor, GetSetores, SetSetor } from '@/modules/structure/setor/setor.service';
import { EditadoPorRodape } from '@/shared/components/EditadoPorRodape';
import { GetPredios } from '@/modules/structure/predio/predio.service';
import { useNavigate, useParams } from 'react-router-dom';
import { SelectGenerico } from '@/shared/components/forms/SelectGenerico';
import { SubmitHandler, useForm, type Resolver } from 'react-hook-form';
import { Tabelas } from '@/shared/constants/tables';
import { setorFormSchema } from '@/modules/structure/setor/setor.schema';
import { FieldError, formControlClass, formSelectClass } from '@/shared/components/forms/FormFieldFeedback';
import { numberInputValueAs } from '@/shared/utils/formZod.utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { useEffect, useMemo, useState } from 'react';

export default function SetorEditPage() {
  const { setActions } = useBreadcrumbActions();
  const navigate = useNavigate();
  const params = useParams();

  const [modalExcluirShow, setModalExcluirShow] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const id = params.id ? Number(params.id) : 0;
  const btnSalvar = id > 0 ? 'Salvar' : 'Cadastrar';

  useEffect(() => {
    setActions({
      secondary: { label: 'Novo', href: Links.Setor_Criar, onClick: () => {} },
      history: { label: 'Histórico', onClick: () => setModalHistorico(true) },
    });
    return () => setActions({});
  }, [setActions]);

  const [loaded, setLoaded] = useState(false);
  const [predios, setPredios] = useState<ReadPredioDto[]>([]);
  const [setores, setSetores] = useState<ReadSetorDto[]>([]);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<ReadSetorDto>({
    resolver: zodResolver(setorFormSchema) as unknown as Resolver<ReadSetorDto>,
    mode: 'onTouched',
    defaultValues: {
      sigla: '',
      nome: '',
      capacidade: 0,
      predio: undefined,
      pai: undefined,
    },
  });

  const pai = watch('pai');
  const predioId = watch('predio.id');
  const editadoPor = watch('editadoPor');
  const editadoEm = watch('editadoEm');

  const setoresPaiOpcoes = useMemo(() => {
    return setores.filter((setor) => {
      if (id > 0 && setor.id === id) return false;
      if (predioId && setor.predio?.id && Number(setor.predio.id) !== Number(predioId)) {
        return false;
      }
      return true;
    });
  }, [setores, id, predioId]);

  useEffect(() => {
    Promise.all([
      GetPredios(0, 1000),
      GetSetores({ skip: 0, take: 5000, comPredio: true }),
      id > 0
        ? GetSetor(id)
          .then((result) => {
            if (result) {
              reset(result);
            }
          })
          .catch((res) => {
            toast.error(String(res));
          })
        : Promise.resolve(),
    ])
      .then(([prediosResult, setoresResult]) => {
        setPredios(prediosResult.objects);
        setSetores(setoresResult?.objects ?? []);
        setLoaded(true);
      })
      .catch((res) => {
        toast.error(String(res));
      });
  }, [id, reset]);

  const onInvalid = () => {
    toast.error('Corrija os campos destacados antes de salvar.');
  };

  const onSubmit: SubmitHandler<ReadSetorDto> = (data) => {
    setIsLoading(true);

    const dataToSave: CreateSetorDto = {
      id: id > 0 ? id : undefined,
      sigla: data.sigla || '',
      nome: data.nome || '',
      capacidade: Number(data.capacidade) || 0,
      predio: { id: Number(data.predio?.id) },
      pai: pai?.id ? { id: Number(pai.id) } : null,
    } as CreateSetorDto;

    SetSetor(dataToSave)
      .then((res) => {
        toast.success(String(res));
        navigate(Links.Setor_Index);
      })
      .catch((res) => {
        toast.error(String(res));
      })
      .finally(() => setIsLoading(false));
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setModalExcluirShow(false);
    DeleteSetor(id)
      .then((res) => {
        toast.success(String(res));
        navigate(Links.Setor_Index);
      })
      .catch((res) => {
        toast.error(String(res));
      });
  };

  if (!loaded) {
    return <Loading />;
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3>Setor</h3>
        </div>
      </div>
      <hr className="pb-4" />

      <form onSubmit={(event) => { void handleSubmit(onSubmit, onInvalid)(event); }}>
        <div className="row">
          <div className="col-md-2 mb-3">
            <label htmlFor="sigla" className="form-label">
              Sigla/Número:{' '}
              <span className="text-danger">*</span>
            </label>
            <input
              id="sigla"
              type="text"
              title="Sigla ou número do setor/sala"
              className={formControlClass(!!errors.sigla)}
              maxLength={7}
              {...register('sigla')}
            />
            <FieldError message={errors.sigla?.message} />
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="nome" className="form-label">
              Nome:{' '}
              <span className="text-danger">*</span>
            </label>
            <input
              id="nome"
              type="text"
              title="Nome do setor"
              className={formControlClass(!!errors.nome)}
              {...register('nome')}
            />
            <FieldError message={errors.nome?.message} />
          </div>

          <div className="col-md-2 mb-3">
            <label htmlFor="capacidade" className="form-label">
              Capacidade:{' '}
              <span className="text-danger">*</span>
            </label>
            <input
              id="capacidade"
              type="number"
              title="Capacidade de pessoal"
              className={formControlClass(!!errors.capacidade)}
              min={1}
              max={999}
              {...register('capacidade', { setValueAs: numberInputValueAs })}
            />
            <FieldError message={errors.capacidade?.message} />
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="predio" className="form-label">
              Unidade:{' '}
              <span className="text-danger">*</span>
            </label>
            <select
              id="predio"
              title="Unidade"
              className={formSelectClass(!!errors.predio?.id)}
              {...register('predio.id', {
                setValueAs: numberInputValueAs,
                onChange: () => {
                  if (pai?.id) {
                    setValue('pai', undefined);
                  }
                },
              })}
            >
              <option value="">Selecione...</option>
              {predios.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nome}
                </option>
              ))}
            </select>
            <FieldError message={errors.predio?.id?.message} />
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="pai" className="form-label">
              Setor pai:
            </label>
            <SelectGenerico
              id="pai"
              name="pai"
              value={pai as ReadSetorDto | undefined}
              onChange={(e) => setValue('pai', e as ReadSetorDto | undefined)}
              items={setoresPaiOpcoes}
              getOptionLabel={(s) => s.nome || s.sigla || String(s.id)}
              getOptionValue={(s) => s.id}
              isClearable={true}
            />
            <FieldError message={errors.pai?.id?.message} />
          </div>

          <EditadoPorRodape
            dados={{
              editadoPor: editadoPor ?? undefined,
              editadoEm: editadoEm,
            }}
          />

          <div>
            <BotoesSalvarExcluir
              isLoading={isLoading}
              texto={btnSalvar}
              textoLoading={btnSalvar === 'Cadastrar' ? ' Cadastrando...' : ' Salvando...'}
              id={id}
              onExcluirClick={() => setModalExcluirShow(true)}
            />
          </div>
        </div>
      </form>

      <ModalExcluir
        show={modalExcluirShow}
        handleExcluir={handleExcluir}
        handleHide={() => setModalExcluirShow(false)}
      />
      <ModalHistorico
        show={modalHistorico}
        handleHide={() => setModalHistorico(false)}
        tabela={Tabelas.setor}
        idRegistro={id}
      />
    </>
  );
}
